import { IconCode } from "@/assets/icons/geist/IconCode";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/flexkit/Collapsible";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { cn } from "@/components/podkit/lib/cn";
import { Text } from "@/components/podkit/typography/Text";
import type { AgentMessageFragmentFileModification } from "@/hooks/use-stream-conversation";
import { useMemo, type FC } from "react";

export const FragmentFileModification: FC<{
    fragment: AgentMessageFragmentFileModification;
    defaultOpen: boolean;
}> = ({ fragment, defaultOpen = false }) => {
    const diff = useMemo(() => parseDiffContent(fragment.content), [fragment.content]);
    return (
        <Collapsible defaultOpen={defaultOpen}>
            <div className="flex flex-col overflow-hidden rounded-lg border border-border-base">
                <CollapsibleTrigger className="w-full">
                    <FragmentFileModificationHeader filePath={fragment.filePath} diff={diff} />
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <FragmentFileModificationDiff fragment={fragment} diff={diff} />
                </CollapsibleContent>
            </div>
        </Collapsible>
    );
};

const FragmentFileModificationHeader: FC<{
    filePath: string;
    diff?: SearchReplaceDiff;
}> = ({ filePath, diff }) => {
    return (
        <div className="flex items-center justify-between bg-surface-02 p-2">
            <div className="flex min-w-0 items-center gap-2">
                <IconCode size="base" className="shrink-0 text-content-secondary" />
                <Text className="truncate text-sm font-bold text-content-secondary">{filePath}</Text>
            </div>
            {diff?.stats && (
                <div className="flex shrink-0 items-center gap-1 text-sm">
                    <span className="text-content-positive">+{diff.stats.additions}</span>
                    <span className="text-content-red">-{diff.stats.deletions}</span>
                </div>
            )}
        </div>
    );
};

const FragmentFileModificationDiff: FC<{
    fragment: AgentMessageFragmentFileModification;
    diff: SearchReplaceDiff;
}> = ({ fragment, diff }) => {
    return (
        <ScrollArea orientation="both" className="flex max-h-[300px] border-t border-border-base p-0">
            <div className="flex flex-grow flex-col" data-testid={`fragment-diff-${fragment.actionId}`}>
                <pre className="flex flex-col bg-surface-red p-2">
                    {diff.search.split("\n").map((line, lineIndex) => (
                        <DiffLine key={`search-${lineIndex}`} line={line} type="search" />
                    ))}
                </pre>
                <pre className="flex flex-col bg-surface-green p-2">
                    {diff.replace.split("\n").map((line, lineIndex) => (
                        <DiffLine key={`replace-${lineIndex}`} line={line} type="replace" />
                    ))}
                </pre>
            </div>
        </ScrollArea>
    );
};

const DiffLine: FC<{ line: string; type: "search" | "replace" }> = ({ line, type }) => {
    const prefix = type === "search" ? "-" : "+";
    return (
        <div
            className={cn("flex gap-2 font-mono text-sm", {
                "text-content-red": type === "search",
                "text-content-positive": type === "replace",
            })}
        >
            <span className={cn("w-2 shrink-0 select-none text-center")}>{prefix}</span>
            <span>{line}</span>
        </div>
    );
};

type SearchReplaceDiff = {
    search: string;
    replace: string;
    stats: {
        additions: number;
        deletions: number;
    };
};

const parseDiffContent = (content: string): SearchReplaceDiff => {
    const searchMarker = "<<<<<<< SEARCH";
    const separatorMarker = "=======";
    const replaceMarker = ">>>>>>> REPLACE";

    const searchStart = content.indexOf(searchMarker);
    const separatorStart = content.indexOf(separatorMarker);
    const replaceEnd = content.indexOf(replaceMarker);

    if (searchStart === -1 || separatorStart === -1 || replaceEnd === -1) {
        return {
            search: "",
            replace: "",
            stats: { additions: 0, deletions: 0 },
        };
    }

    const searchContent = content.slice(searchStart + searchMarker.length, separatorStart).trim();
    const replaceContent = content.slice(separatorStart + separatorMarker.length, replaceEnd).trim();

    const searchLines = searchContent.split("\n").filter((line) => line.trim()).length;
    const replaceLines = replaceContent.split("\n").filter((line) => line.trim()).length;

    return {
        search: searchContent,
        replace: replaceContent,
        stats: {
            additions: replaceLines,
            deletions: searchLines,
        },
    };
};
